/**
 * Free-tool registry for the /tools/* SEO matrix.
 *
 * Each tool ships at /tools/{slug} and gets a per-state variant at
 * /tools/{slug}/{state-slug} that pre-fills state-specific defaults and
 * renders state-specific copy. The matrix gives us 6 × 51 = 306 indexable
 * tool pages on top of the 6 tool index pages.
 *
 * Tools deliberately use only state-rules data we already have — no
 * external APIs or LLM calls — so they remain dependency-light and
 * predictable.
 */

export type ToolVertical =
  | "self_storage"
  | "multifamily"
  | "vehicle_storage"
  | "parking";

export interface ToolMeta {
  slug: string;
  name: string;
  /** Which vertical the tool serves. Drives the /tools index grouping
   * + the SEO sitemap matrix (each tool × 51 states × 1 vertical). */
  vertical: ToolVertical;
  /** Marketing one-liner shown on the /tools index card. */
  blurb: string;
  /** Three-word category tag for grouping. */
  category: "Lease" | "Billing" | "Marketing" | "Operations";
  /** Optional icon hint used by ToolsIndexPage. */
  icon: "wallet" | "alert" | "ext" | "grid" | "info" | "download";
  /** Returns the page H1 — `state?` is optional state-variant context. */
  h1: (stateName?: string) => string;
  /** Returns the meta description; same convention. */
  metaDescription: (stateName?: string) => string;
  /** A 60-word lead paragraph rendered above the tool widget. */
  lead: (stateName?: string) => string;
}

export const TOOLS: ToolMeta[] = [
  {
    slug: "late-fee-calculator",
    name: "Late-fee calculator",
    vertical: "self_storage",
    blurb:
      "Punch in monthly rent + your state. Get the statutory late-fee cap, the grace window, and when the fee can post. Works across multifamily, self-storage, parking, and RV/boat.",
    category: "Billing",
    icon: "wallet",
    h1: (s) =>
      s
        ? `${s} late-fee calculator`
        : `Late-fee calculator (every state)`,
    metaDescription: (s) =>
      s
        ? `${s} late-fee cap and grace-period calculator across multifamily, self-storage, parking, and RV/boat. Statute-cited. Plug in monthly rent and get the maximum fee you can legally assess.`
        : `Late-fee cap and grace-period calculator across multifamily, self-storage, parking, and RV/boat — every U.S. market. Plug in monthly rent and get the legal maximum.`,
    lead: (s) =>
      s
        ? `${s} caps late fees by statute — the cap is most explicitly cited in the self-storage statute below, and lands within a few dollars of the same number under the landlord-tenant act, parking lease law, and marina/RV agreement law. Punch in your monthly rent and the calculator returns the maximum fee you can legally charge, the grace-period window after the due date, and the first day a formal notice can post.`
        : `Every state caps late fees by statute. The cap is most explicitly cited in each state's self-storage statute — it lands within a few dollars of the same number under the landlord-tenant act (multifamily), parking lease law, and marina/RV agreement law. Pick a state and plug in your monthly rent for the legal maximum, the grace window, and the first day a formal notice can post.`,
  },
  {
    slug: "lien-sale-timeline",
    name: "Lien-sale timeline",
    vertical: "self_storage",
    blurb:
      "Last-payment date plus your state → notice dates, sale window, and a certified-mail template you can copy.",
    category: "Lease",
    icon: "alert",
    h1: (s) =>
      s
        ? `${s} self-storage lien-sale timeline builder`
        : `Self-storage lien-sale timeline builder (every state)`,
    metaDescription: (s) =>
      s
        ? `Build a ${s}-compliant lien-sale timeline. Default-notice date, lien-notice date, sale-eligible date — all from the last-payment date. Statute-cited.`
        : `Build a state-compliant lien-sale timeline for any U.S. self-storage market. Default-notice date, lien-notice date, sale-eligible date — all from the last-payment date.`,
    lead: (s) =>
      s
        ? `Tenants who haven't paid eventually become a lien-sale matter. ${s} has specific timing for the default notice, the formal lien notice, and the sale-eligible window. Enter the last-payment date and the calculator walks the timeline forward.`
        : `Tenants who haven't paid eventually become a lien-sale matter. Every state has specific timing for the default notice, the formal lien notice, and the sale-eligible window. Pick a state, enter the last-payment date, and the calculator walks the timeline forward.`,
  },
  {
    slug: "move-out-notice-calculator",
    name: "Move-out notice calculator",
    vertical: "self_storage",
    blurb:
      "How much notice does a customer have to give you before they vacate? State-specific minimums across multifamily, self-storage, parking, and RV/boat.",
    category: "Operations",
    icon: "info",
    h1: (s) =>
      s
        ? `${s} move-out notice calculator`
        : `Move-out notice calculator (every state)`,
    metaDescription: (s) =>
      s
        ? `${s} move-out notice minimum across multifamily, self-storage, parking, and RV/boat. Customer intends to vacate on a date — calculate the earliest legal notice date.`
        : `Move-out notice minimum for every U.S. state across multifamily, self-storage, parking, and RV/boat. Pick the state, the intended vacate date, get the earliest legal notice date.`,
    lead: (s) =>
      s
        ? `${s} sets the minimum notice a customer must give before vacating — a resident in multifamily, a tenant in self-storage, a permit-holder in parking, or a boater / RV operator in vehicle storage. Plug in the planned move-out date and the calculator returns the latest date a customer can hand you notice and still walk out clean.`
        : `Most states set a minimum notice a customer must give before vacating — applies across multifamily, self-storage, parking, and RV/boat agreements. Pick a state, plug in the planned move-out date, and the calculator returns the latest date a customer can hand you notice and still walk out clean.`,
  },
  {
    slug: "unit-size-calculator",
    name: "Unit-size calculator",
    vertical: "self_storage",
    blurb:
      "Tenant ticks items they're storing. Tool recommends 5×5, 5×10, 10×10, 10×15, 10×20, or 10×30.",
    category: "Marketing",
    icon: "grid",
    h1: (s) =>
      s
        ? `${s} self-storage unit size calculator`
        : `Self-storage unit size calculator`,
    metaDescription: (s) =>
      s
        ? `${s} self-storage unit-size calculator. Tick the items you're storing — get a unit-size recommendation (5×5, 5×10, 10×10, 10×15, 10×20, 10×30).`
        : `Free unit-size calculator for self-storage tenants. Tick the items you're storing — get a unit-size recommendation (5×5, 5×10, 10×10, 10×15, 10×20, 10×30).`,
    lead: (s) =>
      s
        ? `Tenants chronically over- or under-estimate the unit size they need. Tick what you're storing and the calculator returns the smallest unit that comfortably fits ${s ? `the typical ${s} household load` : "your inventory"} — and the next size up if you'd like breathing room.`
        : `Tenants chronically over- or under-estimate the unit size they need. Tick what you're storing and the calculator returns the smallest unit that comfortably fits — and the next size up if you'd like breathing room.`,
  },
  {
    slug: "storage-rate-finder",
    name: "Storage rate finder",
    vertical: "self_storage",
    blurb:
      "Punch in unit size + ZIP — get a ballpark market rate based on national averages.",
    category: "Marketing",
    icon: "ext",
    h1: (s) =>
      s
        ? `${s} self-storage rate finder`
        : `Self-storage rate finder (all U.S. markets)`,
    metaDescription: (s) =>
      s
        ? `${s} self-storage rate finder. Unit size + ZIP → ballpark market rate. Use as a sanity check before you publish a new rate sheet.`
        : `Find a ballpark self-storage rate for any U.S. market. Unit size + ZIP → estimated monthly rent. Useful for operators sanity-checking a rate sheet or tenants pricing a move.`,
    lead: (s) =>
      s
        ? `Knowing where the market sits before you set a rate sheet is the difference between leaving money on the table and pricing yourself out. Use this as a sanity check for ${s} — punch in unit size and the calculator returns the typical band based on national averages.`
        : `Knowing where the market sits before you set a rate sheet is the difference between leaving money on the table and pricing yourself out. Punch in unit size + ZIP — get the typical price band for that market based on national averages.`,
  },
  {
    slug: "self-storage-tax-lookup",
    name: "Self-storage tax lookup",
    vertical: "self_storage",
    blurb:
      "State-by-state sales tax on self-storage rent. Some states tax it, some don't.",
    category: "Billing",
    icon: "download",
    h1: (s) =>
      s
        ? `${s} self-storage sales tax lookup`
        : `Self-storage sales tax lookup (every state)`,
    metaDescription: (s) =>
      s
        ? `Does ${s} tax self-storage rent? Yes or no, the rate, and the citation. A reference for operators setting up billing.`
        : `Which states tax self-storage rent? A clean reference table — yes / no, the rate, the citation. For operators setting up billing in a new market.`,
    lead: (s) =>
      s
        ? `Sales tax on self-storage rent isn't uniform across the U.S. Some states exempt it entirely; others tax it at the standard rate. Here's where ${s} lands.`
        : `Sales tax on self-storage rent isn't uniform across the U.S. Some states exempt it entirely; others tax it at the standard rate; a few apply a special rate. Use this table as your billing-setup reference.`,
  },
  // ====================================================================
  // MULTIFAMILY TOOLS
  // ====================================================================
  {
    slug: "security-deposit-cap",
    name: "Security deposit cap",
    vertical: "multifamily",
    blurb:
      "How much can a landlord charge as a security deposit in your state? Statutory caps + return-window rules.",
    category: "Lease",
    icon: "wallet",
    h1: (s) =>
      s
        ? `${s} security deposit cap & return rules`
        : `Security deposit caps for every U.S. state`,
    metaDescription: (s) =>
      s
        ? `${s} security deposit cap, return window, itemization rules, and penalty for late return. Statute-cited reference for multifamily operators.`
        : `Security deposit caps and return-window rules for every U.S. state. Statute-cited reference for multifamily landlords and property managers.`,
    lead: (s) =>
      s
        ? `${s} caps how much a landlord can hold as a security deposit and sets the timeline for returning it after move-out. Get the max deposit, the return window, the itemization requirement, and the penalty for blowing the deadline.`
        : `Every state caps how much a landlord can hold as a security deposit and sets the return window after move-out. Pick a state — get the max deposit, the return window, the itemization requirement, and the penalty for missing the deadline.`,
  },
  {
    slug: "rent-affordability-calculator",
    name: "Rent affordability calculator",
    vertical: "multifamily",
    blurb:
      "Resident's gross monthly income → max rent they can comfortably afford (30%, 40%, 50% rules).",
    category: "Marketing",
    icon: "wallet",
    h1: (s) =>
      s
        ? `${s} rent affordability calculator`
        : `Rent affordability calculator — 30%, 40%, 50% rules`,
    metaDescription: (s) =>
      s
        ? `${s} rent-to-income calculator. Resident's gross income → max recommended rent. Useful for screening and lease-offer pricing.`
        : `Rent-to-income calculator for residents and landlords. Plug in gross monthly income, get the 30/40/50% affordability bands. Use for screening and offer pricing.`,
    lead: (s) =>
      s
        ? `Most landlords screen residents at a 3× rent-to-income ratio (rent ≤ 33% of gross). ${s} markets often demand a tighter band. Plug in the applicant's monthly income — get the 30%, 40%, and 50% bands and a pass/fail signal against your house ratio.`
        : `Most landlords screen residents at a 3× rent-to-income ratio (rent ≤ 33% of gross). Plug in the applicant's monthly income — get the 30%, 40%, and 50% bands so you can decide where your house ratio sits.`,
  },
  {
    slug: "eviction-timeline-builder",
    name: "Eviction timeline builder",
    vertical: "multifamily",
    blurb:
      "Notice → court filing → judgment → writ → sheriff lockout. State-specific minimums.",
    category: "Operations",
    icon: "alert",
    h1: (s) =>
      s
        ? `${s} eviction timeline builder`
        : `Eviction timeline builder for every U.S. state`,
    metaDescription: (s) =>
      s
        ? `${s} eviction timeline. Notice-to-quit duration, court-filing window, judgment timing, writ-of-possession window. Statute-cited.`
        : `Build a state-compliant eviction timeline. Notice-to-quit duration, court filing window, judgment timing, writ window. Statute-cited per state.`,
    lead: (s) =>
      s
        ? `${s} has specific timing for the notice-to-quit, court filing, judgment, and lockout windows. Plug in the date of the missed payment and the calculator walks the timeline forward to the earliest legal lockout date.`
        : `Every state has specific timing for the notice-to-quit, court filing, judgment, and lockout windows. Plug in the date of the missed payment and the calculator walks the timeline forward to the earliest legal lockout date.`,
  },
  {
    slug: "fair-housing-checklist",
    name: "Fair housing checklist",
    vertical: "multifamily",
    blurb:
      "Federal + state protected classes. A pre-screening checklist so your application questions stay compliant.",
    category: "Lease",
    icon: "info",
    h1: (s) =>
      s
        ? `${s} fair housing protected classes & screening checklist`
        : `Fair housing protected classes (federal + every state)`,
    metaDescription: (s) =>
      s
        ? `${s} fair housing protected classes plus federal protections. A screening-question checklist so your application stays compliant.`
        : `Federal fair housing protected classes plus the state-specific additions in every U.S. state. A screening-question checklist for compliant applications.`,
    lead: (s) =>
      s
        ? `Federal fair housing law protects 7 classes; ${s} likely protects more. Walk through the checklist before you finalize your application — flag any question that touches a protected class so you can drop it or reframe it.`
        : `Federal fair housing law protects 7 classes; most states protect more. Walk through this checklist before you finalize your application — flag any question that touches a protected class so you can drop or reframe it.`,
  },
  {
    slug: "pet-policy-builder",
    name: "Pet policy + pet rent calculator",
    vertical: "multifamily",
    blurb:
      "Pet deposit cap, monthly pet rent, breed/weight restrictions, ESA/service-animal rules.",
    category: "Lease",
    icon: "info",
    h1: (s) =>
      s
        ? `${s} pet policy and pet rent calculator for multifamily`
        : `Pet policy + pet rent calculator for multifamily landlords`,
    metaDescription: (s) =>
      s
        ? `${s} pet-deposit cap, monthly pet rent norms, breed restrictions, and ESA/service-animal rules. A reference for multifamily lease addenda.`
        : `Pet-deposit caps, monthly pet rent norms, breed restrictions, and ESA/service-animal rules per state. Reference for multifamily lease addenda.`,
    lead: (s) =>
      s
        ? `Pet policies are a leasing tool and a fair-housing minefield. ${s} caps how much you can hold as a pet deposit and restricts how you treat assistance animals. Build a compliant policy + price the monthly pet rent against your market.`
        : `Pet policies are a leasing tool and a fair-housing minefield. Caps on pet deposits and rules for assistance animals vary by state. Build a compliant policy + price the monthly pet rent against your market.`,
  },
  // ====================================================================
  // RV / BOAT STORAGE TOOLS
  // ====================================================================
  {
    slug: "slip-loa-pricing-calculator",
    name: "Slip LOA pricing calculator",
    vertical: "vehicle_storage",
    blurb:
      "Vessel length overall → annual slip rent at typical $/ft/mo bands. Compare wet vs dry stack.",
    category: "Billing",
    icon: "wallet",
    h1: (s) =>
      s
        ? `${s} marina slip pricing calculator (per-foot)`
        : `Marina slip pricing calculator — $/ft/mo by LOA`,
    metaDescription: (s) =>
      s
        ? `${s} marina slip pricing. Vessel LOA → typical $/ft/mo wet slip and dry stack rate. Annual + monthly + seasonal calculations.`
        : `Marina slip price calculator. Vessel LOA → typical $/ft/mo for wet slip and dry stack. Annual + monthly + seasonal calculations.`,
    lead: (s) =>
      s
        ? `Marina slips are priced per foot of LOA per month. ${s} markets typically run $11–$18/ft/mo for wet slips and $14–$28/ft/mo for dry stack. Plug in vessel length — get the monthly, seasonal (6mo), and annual cost across the wet/dry split.`
        : `Marina slips are priced per foot of LOA per month. Plug in vessel length — get the monthly, seasonal (6mo), and annual cost across the wet/dry split. Useful for boaters comparing marinas and operators setting a fresh rate sheet.`,
  },
  {
    slug: "boat-insurance-checklist",
    name: "Boat insurance checklist",
    vertical: "vehicle_storage",
    blurb:
      "Minimum liability, hull coverage, named-storm rider — what marinas typically require before issuing a slip code.",
    category: "Lease",
    icon: "info",
    h1: (s) =>
      s
        ? `${s} boat insurance requirements for marina slip rental`
        : `Boat insurance checklist for marina slip rental`,
    metaDescription: (s) =>
      s
        ? `${s} marina slip insurance requirements. Minimum liability, hull coverage, named-storm rider, fuel-spill rider. Carrier checklist.`
        : `Marina slip insurance checklist. Minimum liability ($300k typical), hull coverage, named-storm rider, fuel-spill rider — what most marinas require.`,
    lead: (s) =>
      s
        ? `Marinas typically require proof of marine insurance before issuing a slip code. ${s} marinas typically ask for $300k liability minimum, hull coverage if you store the boat in-water, and a named-storm rider if you're in a hurricane zone. Use this as a pre-renewal checklist.`
        : `Marinas typically require proof of marine insurance before issuing a slip code. The usual ask: $300k liability minimum, hull coverage if storing in-water, named-storm rider in hurricane zones, fuel-spill rider for older boats. Use this as a pre-renewal checklist.`,
  },
  {
    slug: "rv-storage-size-guide",
    name: "RV storage size guide",
    vertical: "vehicle_storage",
    blurb:
      "Class A / B / C / fifth-wheel / travel trailer dimensions → smallest covered or uncovered spot that fits.",
    category: "Marketing",
    icon: "grid",
    h1: (s) =>
      s
        ? `${s} RV storage size guide — Class A, B, C, fifth-wheel`
        : `RV storage size guide for tenants and operators`,
    metaDescription: (s) =>
      s
        ? `${s} RV storage size guide. Class A/B/C, fifth-wheel, travel trailer dimensions → smallest legal covered or uncovered spot.`
        : `RV storage size guide. Class A/B/C, fifth-wheel, travel trailer dimensions → the smallest legal covered or uncovered spot. For tenants and operators alike.`,
    lead: (s) =>
      s
        ? `RVs come in five common form factors, each with a typical length-overall band. Pick yours — the calculator returns the smallest spot that comfortably fits in a ${s} climate (with snow accumulation or sun exposure factored in).`
        : `RVs come in five common form factors — Class A, B, C, fifth-wheel, travel trailer — each with a typical length band. Pick yours — get the smallest spot that comfortably fits with snow accumulation or sun exposure factored in.`,
  },
  {
    slug: "marina-tax-lookup",
    name: "Marina + boat-storage sales tax lookup",
    vertical: "vehicle_storage",
    blurb:
      "Does your state tax marina slip rent? Boat-storage sales tax band per state.",
    category: "Billing",
    icon: "download",
    h1: (s) =>
      s
        ? `${s} marina + boat storage sales tax lookup`
        : `Marina + boat-storage sales tax lookup (every state)`,
    metaDescription: (s) =>
      s
        ? `Does ${s} tax marina slip rent? Boat-storage sales tax band, exemption posture, citation. Reference for operators setting up billing.`
        : `Marina + boat-storage sales tax by state. Yes / no, the rate, the citation. Reference for operators setting up billing.`,
    lead: (s) =>
      s
        ? `Sales tax on marina slip rent isn't uniform across the U.S. Some states exempt it as a personal-property service; others tax it at the standard rate. Here's where ${s} lands.`
        : `Sales tax on marina slip rent isn't uniform across the U.S. Some states exempt it as a personal-property service; others tax it at the standard rate; a few apply a special marine-fuel-adjacent rate. Use this table as your billing-setup reference.`,
  },
  // ====================================================================
  // PARKING TOOLS
  // ====================================================================
  {
    slug: "parking-permit-pricing",
    name: "Parking permit pricing benchmarks",
    vertical: "parking",
    blurb:
      "Surface vs structured · monthly vs daily · ZIP-based bands for parking permit pricing.",
    category: "Marketing",
    icon: "ext",
    h1: (s) =>
      s
        ? `${s} monthly parking permit pricing benchmarks`
        : `Monthly parking permit pricing benchmarks (U.S.)`,
    metaDescription: (s) =>
      s
        ? `${s} monthly parking permit pricing. Surface vs structured · downtown vs suburb. Useful for operators setting a fresh permit rate.`
        : `Monthly parking permit pricing benchmarks across U.S. markets. Surface vs structured · downtown vs suburb · daily vs monthly. Useful for operators setting a fresh rate.`,
    lead: (s) =>
      s
        ? `Permit pricing varies wildly between markets — a ${s} downtown stall can run 5× a same-state suburban stall. Plug in your ZIP and stall type — get the typical permit price band based on national averages.`
        : `Permit pricing varies wildly between markets — a downtown stall can run 5× a same-state suburban stall. Plug in your ZIP and stall type — get the typical permit price band based on national averages.`,
  },
  {
    slug: "parking-tax-lookup",
    name: "Parking sales tax lookup",
    vertical: "parking",
    blurb:
      "State-by-state sales tax on parking rent. Some states tax it heavily; some don't tax it at all.",
    category: "Billing",
    icon: "download",
    h1: (s) =>
      s
        ? `${s} parking sales tax lookup`
        : `Parking sales tax lookup (every state)`,
    metaDescription: (s) =>
      s
        ? `Does ${s} tax monthly parking rent? Daily/transient rate, monthly exemption posture, citation.`
        : `Parking sales tax by state. Yes / no, the rate, daily vs monthly distinctions, and the citation.`,
    lead: (s) =>
      s
        ? `Sales tax on parking is one of the most aggressively-taxed services in many states. ${s} either taxes parking at a special rate (commonly 18–22%), at the standard rate, or exempts monthly permits while taxing transient daily parking. Here's where ${s} lands.`
        : `Sales tax on parking is one of the most aggressively-taxed services in many states. Some states apply a special parking rate (commonly 18–22%); some tax it at the standard rate; some exempt monthly permits while taxing daily/transient parking.`,
  },
  {
    slug: "towing-rules-by-state",
    name: "Towing rules by state",
    vertical: "parking",
    blurb:
      "Notice requirements, photo-evidence rules, max impound fees, return windows.",
    category: "Operations",
    icon: "alert",
    h1: (s) =>
      s
        ? `${s} parking-lot towing rules and notice requirements`
        : `Parking-lot towing rules by state`,
    metaDescription: (s) =>
      s
        ? `${s} parking-lot towing rules. Signage, photo-evidence, max impound fee, return-window requirements. Operator reference.`
        : `Parking-lot towing rules per state. Signage, photo-evidence, max impound fee, return-window requirements. Reference for private-lot operators.`,
    lead: (s) =>
      s
        ? `Towing from a private parking lot has serious procedural requirements in most states. ${s} requires specific signage, photo evidence at the time of tow, a maximum impound fee, and a tight return window. Use this as a compliance check before you sign with a towing partner.`
        : `Towing from a private parking lot has serious procedural requirements in most states. Signage, photo evidence at the time of tow, max impound fees, return windows. Use this as a compliance check before you sign with a towing partner.`,
  },
  {
    slug: "parking-rate-card-builder",
    name: "Parking rate-card builder",
    vertical: "parking",
    blurb:
      "Build a transient + monthly rate card from local benchmarks. Daily, weekly, monthly bands.",
    category: "Marketing",
    icon: "grid",
    h1: (s) =>
      s
        ? `${s} parking rate-card builder — transient and monthly`
        : `Parking rate-card builder — transient + monthly`,
    metaDescription: (s) =>
      s
        ? `${s} parking rate-card builder. Daily, weekly, and monthly rate bands tuned to local benchmarks. Operator tool.`
        : `Parking rate-card builder. Daily, weekly, and monthly rate bands tuned to local benchmarks. For operators setting a fresh kiosk + permit price.`,
    lead: (s) =>
      s
        ? `Most lots run a transient + monthly split — kiosk-paid daily traffic plus a permit-holder base. ${s} markets vary widely on the daily-to-monthly ratio. Build a coherent rate card across daily, weekly, monthly bands tuned to your ZIP.`
        : `Most lots run a transient + monthly split — kiosk-paid daily traffic plus a permit-holder base. Build a coherent rate card across daily, weekly, monthly bands tuned to your local benchmark.`,
  },
];

export const TOOL_BY_SLUG: Record<string, ToolMeta> = Object.fromEntries(
  TOOLS.map((t) => [t.slug, t]),
);
